"use client"

import type React from "react"
import { useState } from "react"
import type { SyncOption, FieldMapping, SetupData } from "../SetupFlow"
import "./MapFields.css"

interface MapFieldsProps {
  selectedSyncOptions: SyncOption[]
  onSubmit: (mappings: FieldMapping[]) => void
  onBack: () => void
  setupData: SetupData
}

const MapFields: React.FC<MapFieldsProps> = ({ selectedSyncOptions, onSubmit, onBack, setupData }) => {
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>(setupData.fieldMappings)

  const hubspotFields = [
    "firstname",
    "lastname",
    "email",
    "phone",
    "jobtitle",
    "department",
    "company",
    "mobilephone",
    "address",
    "city",
    "state",
    "country",
    "website",
    "industry",
    "annualrevenue",
    "numberofemployees",
  ]

  const salesforceFields = [
    "FirstName",
    "LastName",
    "Email",
    "Phone",
    "Title",
    "Department",
    "AccountId",
    "MobilePhone",
    "Street",
    "City",
    "State",
    "Country",
    "Website",
    "Industry",
    "AnnualRevenue",
    "NumberOfEmployees",
  ]

  const handleHubSpotFieldChange = (mappingId: string, newField: string) => {
    setFieldMappings((prev) =>
      prev.map((mapping) => (mapping.id === mappingId ? { ...mapping, hubspotField: newField } : mapping)),
    )
  }

  const handleSalesforceFieldChange = (mappingId: string, newField: string) => {
    setFieldMappings((prev) =>
      prev.map((mapping) => (mapping.id === mappingId ? { ...mapping, salesforceField: newField } : mapping)),
    )
  }

  const handleDeleteMapping = (mappingId: string) => {
    setFieldMappings((prev) => prev.filter((mapping) => mapping.id !== mappingId))
  }

  const handleAddFieldMapping = () => {
    const newMapping: FieldMapping = {
      id: Date.now().toString(),
      hubspotField: hubspotFields[0],
      salesforceField: salesforceFields[0],
    }
    setFieldMappings((prev) => [...prev, newMapping])
  }

  const handleSubmit = () => {
    onSubmit(fieldMappings)
  }

  return (
    <div className="map-fields">
      <div className="map-fields-header">
        <h1>Map Your Fields</h1>
        <p>Match fields between Salesforce and HubSpot to ensure your data syncs accurately.</p>
        <p>You can revisit these mappings later.</p>
      </div>

      <div className="mapping-section">
        <div className="section-header">
          <h2>Contacts</h2>
          <p>
            Map contact fields to lead fields to define how your HubSpot contact data will appear in Salesforce leads.
          </p>
        </div>

        <div className="field-mapping-container">
          <div className="field-headers">
            <div className="header-left">
              <h3>Contact Fields - HubSpot</h3>
            </div>
            <div className="header-right">
              <h3>Lead Fields - Salesforce</h3>
            </div>
            <div></div>
          </div>

          <div className="field-mappings">
            {fieldMappings.map((mapping) => (
              <div key={mapping.id} className="field-mapping-row">
                <div className="field-select-container">
                  <select
                    value={mapping.hubspotField}
                    onChange={(e) => handleHubSpotFieldChange(mapping.id, e.target.value)}
                    className="field-select hubspot-field"
                  >
                    {hubspotFields.map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field-select-container">
                  <select
                    value={mapping.salesforceField}
                    onChange={(e) => handleSalesforceFieldChange(mapping.id, e.target.value)}
                    className="field-select salesforce-field"
                  >
                    {salesforceFields.map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  className="delete-mapping-btn"
                  onClick={() => handleDeleteMapping(mapping.id)}
                  title="Delete mapping"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3,6 5,6 21,6" />
                    <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          <div className="add-mapping-section">
            <button className="add-mapping-btn" onClick={handleAddFieldMapping}>
              + Add Field Mapping
            </button>
          </div>
        </div>
      </div>

      <div className="actions-section">
        <button className="back-button" onClick={onBack}>
          Back
        </button>
        <button className="continue-button" onClick={handleSubmit}>
          Continue
        </button>
      </div>
    </div>
  )
}

export default MapFields
